import { useEffect, useRef, type ReactNode } from "react";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { useUser } from "@clerk/react";
import { api } from "../../convex/_generated/api";
import { SessionContext } from "../lib/session";
import { TZ } from "../lib/format";
import { pendingInvite } from "../lib/invite";

/**
 * Resolves who is signed in and which household they are in, once, for the
 * whole app.
 *
 * `useConvexAuth` rather than Clerk's `useAuth`: what matters here is not that
 * Clerk has a session but that Convex has *accepted* its token. Between those
 * two moments every query would come back unauthenticated, and firing
 * `bootstrap` in that gap would throw. This is the hook that waits for the
 * second one.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useConvexAuth();

  const me = useQuery(api.households.me, isAuthenticated ? {} : "skip");

  // Only asked when there is a reason to ask. Everyone already in a household
  // would otherwise re-read the whole roster on every cold start to answer a
  // question that can only matter once in a deployment's life.
  const claimable = useQuery(
    api.households.claimable,
    isAuthenticated && me === null ? {} : "skip",
  );

  const bootstrap = useMutation(api.households.bootstrap);

  /**
   * The name the sign-up screen collected, on its way to the member row.
   *
   * It goes through Clerk rather than straight from that screen to here: these
   * are two different components with a session boundary between them, and
   * `signUp.create` has already stored it. `isLoaded` is load-bearing — the
   * effect below must not run while this is still `undefined`, or the row is
   * written with the email address and there is nothing that would come back to
   * fix it.
   */
  const { user, isLoaded: userLoaded } = useUser();

  /**
   * Guards two things: `useMutation` is not contractually a stable reference,
   * so an effect keyed on it could fire every render; and StrictMode invokes
   * effects twice on mount. `bootstrap` is idempotent and safe to race, but
   * there is no reason to send the write more than once.
   */
  const requested = useRef(false);

  useEffect(() => {
    // Only claim when there is something to claim. `me === null` alone would
    // fire this for every invited person too, who has no household to create
    // and would get an error thrown past them on the way to the join screen.
    //
    // An invite in hand outranks claiming, and the join screen is what spends
    // it. The two can only collide on a deployment whose household is still
    // unclaimed — `seed:invite` mints codes without an account — and there,
    // arriving through someone's link and being handed ownership because the
    // code turned out to be bad is the worse of the two outcomes. A reload
    // with no code in the URL claims as normal.
    if (
      !isAuthenticated ||
      !userLoaded ||
      me !== null ||
      claimable !== true ||
      pendingInvite() !== null ||
      requested.current
    ) {
      return;
    }
    requested.current = true;
    // The client is what knows the zone and the name — the backend would be
    // guessing at both.
    void bootstrap({ timezone: TZ, displayName: user?.firstName ?? undefined }).catch(() => {
      // Someone else claimed it in the gap. `me` stays null and the join
      // screen is the correct place to land, so there is nothing to do here.
    });
  }, [isAuthenticated, userLoaded, user, me, claimable, bootstrap]);

  // Signed out is not a session and not "still loading" — the gate needs to
  // tell them apart, and only `useConvexAuth` knows which this is.
  const value = isLoading || !isAuthenticated ? undefined : me;

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
